/**
 * RecorderPort — microphone capture.
 *
 * This is the ports layer (ARCHITECTURE §3). A microphone is a DEVICE
 * capability with a meaningfully different web and Capacitor story, so it
 * belongs here — unlike `SttPort` and `AiReasoningPort`, which are server-side
 * service boundaries and must never live in `src/platform/` (D-012).
 *
 * HISTORY: this port was born as the M2 / S-02B-i capability probe, whose job
 * was to answer questions about real Android hardware that documentation
 * cannot answer — does permission work, does recording start and stop, and
 * WHAT FORMAT does the WebView actually produce. Those questions were answered
 * on Jerry's real phone (`audio/webm;codecs=opus`, container sniffed as
 * webm-or-matroska), and as of M3 / S-03-03 this same implementation IS the
 * product recorder behind the real Capture screen. The probe screen is
 * retired; the port it validated is what it left behind.
 *
 *   The port still OBSERVES rather than assumes: it reads the MIME type the
 *   recorder reports, and it separately sniffs the first bytes of the produced
 *   blob for a container signature. A WebView that labels its output one thing
 *   and emits another is exactly the kind of surprise worth catching, and only
 *   the second check can catch it.
 *
 * INTERRUPTION SIGNALING — S-03-05, policy from D-018. The port OBSERVES two
 * definitive runtime signals about the active recording and reports them to
 * the caller through `start()`'s optional `onInterruption` callback, at most
 * once per recording: the recorder's own `error` event, and the microphone
 * track's `ended` event. It observes and reports; the PRODUCT response — safe
 * stop and preserve, or no-salvage failure — is the caller's, under D-018.
 * Deliberately NOT observed: `devicechange` (fires for unrelated devices and
 * would stop healthy recordings — D-018 forbids it) and track `mute` (fires
 * spuriously; unreliable as an interruption signal). `stop()` also understands
 * a recording the PLATFORM already finalized (a track that ended lets the UA
 * stop the recorder itself) and one that errored — a finalized recording
 * resolves with its complete audio, an errored one is refused outright
 * (D-017/D-018: an errored container is never trusted).
 *
 * DELIBERATELY ABSENT, still:
 *
 *   * no network call of any kind — no provider, no endpoint, no upload.
 *     Transcription is the caller's business, through `src/data/stt-client.ts`
 *   * no persistence. The blob is held in memory and handed to the caller.
 *     D-010's temporary-audio lifecycle is M4 work and nothing here anticipates it
 *   * no duration cap, soft warning or keep-awake (D-008) — those live in the
 *     Capture screen and `keep-awake.ts` (S-03-04), not this file
 *   * no Capture, no transcript, no schema
 *
 * The port exists so a native recorder, if real hardware ever demands one, can
 * replace this implementation without Capture code changing — a plugin swap
 * behind the port rather than a rewrite (ROADMAP Risk Probe A).
 */

/** What a completed recording actually turned out to be. All measured. */
export interface RecordingResult {
  /** The audio itself. Held in memory only; never written anywhere. */
  readonly blob: Blob;
  /** The MIME type the recorder REPORTED, verbatim. May include `;codecs=`. */
  readonly reportedMimeType: string;
  /** Container identified by sniffing the blob's first bytes — see below. */
  readonly observedContainer: ObservedContainer;
  /** Codec parsed out of the reported MIME type, when it names one. */
  readonly reportedCodec: string | null;
  /** Wall-clock milliseconds between start and stop, measured by the app. */
  readonly elapsedMs: number;
  /** `blob.size`. */
  readonly byteSize: number;
}

/**
 * Container identified from the blob's own bytes.
 *
 * `unknown` is a real, useful answer — it means the bytes did not match any
 * signature we know, which is a finding rather than a failure.
 */
export type ObservedContainer = 'webm-or-matroska' | 'ogg' | 'mp4-or-3gp' | 'wav' | 'unknown';

/**
 * Why the active recording was interrupted, as OBSERVED from the runtime.
 *
 * `recorder-error` — the `MediaRecorder` fired its `error` event. The
 * recording is broken and its audio is of unknown validity (D-017: no salvage).
 * `track-ended`   — the microphone's `MediaStreamTrack` fired `ended`: the
 * input genuinely died (disconnected, revoked). The platform finalizes the
 * recording itself in this case, so the audio up to that point is normally
 * trustworthy and a safe stop() will return it.
 */
export type RecordingInterruption = 'recorder-error' | 'track-ended';

export interface RecorderStartOptions {
  /**
   * Called AT MOST ONCE if this recording is interrupted (D-018 / S-03-05) —
   * never after the port has already been stopped or cancelled, and never on
   * behalf of an earlier, abandoned recording. Observing only; the product
   * response is the caller's.
   */
  onInterruption?: (cause: RecordingInterruption) => void;
}

export interface RecorderPort {
  /** Human-readable implementation name. Diagnostics only, never product UI. */
  readonly name: string;
  /** Whether this runtime has the APIs at all. Says nothing about permission. */
  isSupported(): boolean;
  /** Requests microphone access and begins recording. */
  start(options?: RecorderStartOptions): Promise<void>;
  /** Stops, releases the microphone, and returns what was actually produced. */
  stop(): Promise<RecordingResult>;
  /** Abandons any in-flight recording and releases the microphone. */
  cancel(): void;
}

/**
 * Candidate MIME types, offered in preference order and ONLY if the runtime
 * says it supports them.
 *
 * The list is a preference, not an expectation. If none is supported the
 * recorder is constructed with no `mimeType` at all, letting the platform
 * choose — and whatever it chooses is what gets reported. **Forcing a type the
 * WebView does not support would turn a useful observation into a crash.**
 */
const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
] as const;

/**
 * How long `stop()` waits for the recorder's `stop` event before giving up.
 *
 * I-027 / D-017: the `stop` event is the one recorder exit with no natural
 * bound — a WebView that never fires it would otherwise hang the returned
 * promise forever, presenting as a Stop button that did nothing. Five seconds
 * is far beyond any observed finalization time (real hardware finalized a
 * 17-second recording without measurable delay in S-02B-i) while still being
 * an obvious failure rather than a long wait.
 */
export const RECORDER_STOP_TIMEOUT_MS = 5000;

/**
 * Identify the container from the blob's leading bytes.
 *
 * WHY THIS EXISTS ALONGSIDE THE REPORTED MIME TYPE: the MIME type is a label
 * the implementation applies to itself. The bytes are what it actually
 * produced. On an unfamiliar WebView those can disagree, and a probe that only
 * reported the label would carry that disagreement forward as a fact.
 *
 * Signatures:
 *   1A 45 DF A3   EBML  — WebM / Matroska
 *   4F 67 67 53   OggS  — Ogg
 *   ....66 74 79 70  ftyp at offset 4 — MP4 / 3GP / M4A
 *   52 49 46 46   RIFF  — WAV (with WAVE at offset 8)
 */
export async function sniffContainer(blob: Blob): Promise<ObservedContainer> {
  try {
    const header = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    if (header.length < 4) return 'unknown';

    const at = (offset: number, ...bytes: number[]): boolean =>
      bytes.every((byte, index) => header[offset + index] === byte);

    if (at(0, 0x1a, 0x45, 0xdf, 0xa3)) return 'webm-or-matroska';
    if (at(0, 0x4f, 0x67, 0x67, 0x53)) return 'ogg';
    if (at(4, 0x66, 0x74, 0x79, 0x70)) return 'mp4-or-3gp';
    if (at(0, 0x52, 0x49, 0x46, 0x46) && at(8, 0x57, 0x41, 0x56, 0x45)) return 'wav';
    return 'unknown';
  } catch {
    // A blob we cannot read is a finding, not a crash.
    return 'unknown';
  }
}

/** Pulls the codec out of `audio/webm;codecs=opus`, if one is named. */
export function parseCodec(mimeType: string): string | null {
  const match = /codecs?=("?)([^";]+)\1/i.exec(mimeType);
  return match?.[2]?.trim() ?? null;
}

/** The first candidate this runtime supports, or null to let it choose. */
export function chooseMimeType(
  isTypeSupported: (type: string) => boolean = (type) =>
    typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type),
): string | null {
  for (const candidate of PREFERRED_MIME_TYPES) {
    try {
      if (isTypeSupported(candidate)) return candidate;
    } catch {
      // A runtime that throws on the question simply does not support it.
    }
  }
  return null;
}

/**
 * The Web `MediaRecorder` implementation.
 *
 * NO NATIVE PLUGIN. Verified before writing this, in
 * `node_modules/@capacitor/android/.../BridgeWebChromeClient.java`: Capacitor's
 * own WebChromeClient already handles `AUDIO_CAPTURE` permission requests from
 * the WebView, launching the Android runtime request for `RECORD_AUDIO` and
 * `MODIFY_AUDIO_SETTINGS` and granting the WebView request when the user
 * accepts. `getUserMedia` therefore works through the standard bridge, and a
 * recorder plugin would add a native dependency for something already handled.
 *
 * Both permissions must be DECLARED in AndroidManifest.xml — Android denies a
 * runtime request for a permission the manifest does not declare, which would
 * look exactly like the user refusing.
 */
export function createWebMediaRecorder(
  options: {
    /** Overridable so tests can prove the bound without waiting five seconds. */
    stopTimeoutMs?: number;
  } = {},
): RecorderPort {
  const stopTimeoutMs = options.stopTimeoutMs ?? RECORDER_STOP_TIMEOUT_MS;

  let recorder: MediaRecorder | null = null;
  let stream: MediaStream | null = null;
  let startedAt = 0;

  /**
   * What the CURRENT recording's own events have reported about it — set by
   * per-recording listeners registered in `start()`, read by `stop()`.
   *
   * `stopped` — the recorder's `stop` event fired. The platform fires it AFTER
   * the final `dataavailable`, so when this is true the chunks are complete.
   * It matters when the PLATFORM finalized the recording on its own (a track
   * that ended, S-03-05): by the time the caller's safe stop arrives, the
   * `stop` event it would await has already fired, and waiting for it again
   * would time out and discard perfectly finalized audio.
   * `errored` — the recorder's `error` event fired. An errored recording is
   * never salvaged (D-017 / D-018), even if a `stop` event also arrives.
   *
   * A NEW OBJECT PER RECORDING, same F-1 discipline as `chunks`: listeners
   * close over THEIR recording's object, so a late event from an abandoned
   * recorder can never describe the current one.
   */
  let session: { stopped: boolean; errored: boolean } | null = null;

  /**
   * The CURRENT recording's buffer — a reference to an array created in
   * `start()`, never an array this binding owns.
   *
   * R1 finding F-1: the distinction is the fix. A `dataavailable` listener
   * closes over a BINDING, not over the array the binding happened to hold when
   * the listener was registered. So while this was the only buffer, an
   * abandoned recorder's listener kept writing here — and after a timed-out
   * stop, `chunks` had been repointed at the NEXT recording's array. Recording
   * A's late fragment landed in Recording B's audio, and B's transcript was of
   * an audio file it never produced.
   *
   * Now each recording gets a private array (see `start()`) and its listener
   * writes to THAT array by direct reference. This binding is read-only in
   * spirit: `stop()` reads it to build the blob, and start/cancel/discard
   * REPOINT it. Nothing but the owning recording's own listener can ever write
   * into the array a read finds here.
   */
  let chunks: Blob[] = [];

  /**
   * Stops every track on a SPECIFIC stream, passed in rather than read from the
   * shared reference.
   *
   * That argument is the point of the helper, not an incidental style choice.
   * R1 finding N1 is about a retry replacing `stream` after a failed start: a
   * release that reads "whatever the current stream is" would then close the
   * wrong one. Taking the target as a parameter makes it impossible for either
   * caller to drift back to reading the shared reference.
   */
  const stopTracks = (target: MediaStream | null): void => {
    target?.getTracks().forEach((track) => {
      track.stop();
    });
  };

  /**
   * Release the microphone on a NORMAL exit — stop and cancel — because a live
   * track leaves the OS recording indicator on and makes the app look like it is
   * listening when it is not (PT §20, honesty).
   *
   * The failed-start path does NOT use this; see the catch in `start()`.
   */
  const releaseMicrophone = (): void => {
    stopTracks(stream);
    stream = null;
    recorder = null;
    session = null;
  };

  return {
    name: 'web:MediaRecorder',

    isSupported() {
      return (
        typeof MediaRecorder !== 'undefined' &&
        typeof navigator !== 'undefined' &&
        typeof navigator.mediaDevices?.getUserMedia === 'function'
      );
    },

    async start(options?: RecorderStartOptions) {
      if (recorder) throw new Error('A recording is already in progress.');

      // This is the call that triggers the permission prompt on Android.
      // FROM HERE THE MICROPHONE IS LIVE, and everything below can throw.
      const acquired = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream = acquired;

      try {
        const mimeType = chooseMimeType();

        // THIS recording's private buffer (R1 finding F-1). The listener below
        // captures `captured` — the array itself — so it is incapable of
        // writing anywhere else, for as long as the abandoned recorder lives.
        // A late `dataavailable` from a recording nobody is waiting for any
        // more appends to an array nothing reads, and is collected with it.
        //
        // `chunks` is repointed at the same array so `stop()` reads this
        // recording's audio. Assigned TOGETHER with `recorder`, three lines
        // apart, because the pairing is what makes the read correct: the port
        // refuses a second `start()` while one is in progress, and every exit
        // clears both, so the buffer `stop()` finds always belongs to the
        // recorder it is stopping.
        const captured: Blob[] = [];
        chunks = captured;

        // THIS recording's own status, private for the same F-1 reason.
        const status = { stopped: false, errored: false };
        session = status;

        const created = new MediaRecorder(acquired, mimeType ? { mimeType } : undefined);
        recorder = created;

        /**
         * Report an interruption to the caller — AT MOST ONCE, and only while
         * this recording is still the port's active one (S-03-05 / D-018). A
         * recorder that was stopped, cancelled or superseded may still fire
         * events; its news is stale and nobody is told.
         */
        let interruptionNotified = false;
        const notifyInterruption = (cause: RecordingInterruption): void => {
          if (recorder !== created || interruptionNotified) return;
          interruptionNotified = true;
          options?.onInterruption?.(cause);
        };

        created.addEventListener('dataavailable', (event: BlobEvent) => {
          if (event.data.size > 0) captured.push(event.data);
        });
        // Fires AFTER the final dataavailable, so `stopped` implies the chunks
        // are complete — the fact stop() relies on for a platform-finalized
        // recording.
        created.addEventListener('stop', () => {
          status.stopped = true;
        });
        created.addEventListener('error', () => {
          status.errored = true;
          notifyInterruption('recorder-error');
        });
        // The definitive input-death signal (D-018 case 4). `devicechange` is
        // deliberately NOT observed — it fires for unrelated devices and D-018
        // forbids stopping a healthy recording over it.
        for (const track of acquired.getTracks()) {
          if (typeof track.addEventListener === 'function') {
            track.addEventListener('ended', () => {
              notifyInterruption('track-ended');
            });
          }
        }

        startedAt = performance.now();
        created.start();
      } catch (error) {
        // R1 finding N1. `getUserMedia` has already succeeded, so the
        // microphone is OPEN — and an unsupported option or an OS-level
        // failure in the two lines above would otherwise leave it that way
        // with nothing able to close it. The indicator stays on and the user
        // has no idea why.
        //
        // `acquired` is stopped rather than whatever `stream` currently points
        // at, and that distinction is the finding: a caller that retries after
        // this failure would assign a NEW stream over the module reference, so
        // releasing "the current stream" later would close the wrong one and
        // strand the first forever. The local binding cannot be overwritten.
        stopTracks(acquired);
        stream = null;
        recorder = null;
        chunks = [];
        session = null;

        // Rethrown unchanged. The probe shows the real message, and guessing
        // which failure it was is exactly what the device run is for.
        throw error;
      }
    },

    async stop() {
      const active = recorder;
      if (!active) throw new Error('No recording is in progress.');

      // `stop()` is asynchronous: the final chunk arrives with the last
      // `dataavailable`, and only then does `stop` fire. Resolving earlier
      // would report a byte size missing its own tail.
      //
      // The `finally` below is one half of R1 finding N6: if `active.stop()`
      // throws, the microphone must still be released. The TIMEOUT is the
      // other half — I-027, behaviour approved as D-017: a `stop` event that
      // never arrives must not hang this promise forever. When the bound is
      // hit, the accumulated chunks are DISCARDED, never salvaged: a container
      // whose final metadata was never written is of unknown validity, and
      // sending it would convert an honest local failure into a confusing
      // provider one.

      // The recorder's OWN answer about what it produced. Not our preference,
      // and not what the documentation says it should be.
      //
      // Read here, above the stop sequence, because the `finally` would
      // otherwise have moved this read to after teardown. `mimeType` is set at
      // construction and does not change across `stop()`, so the VALUE is
      // identical either way — but the successful path is the one real hardware
      // has already validated, and reading nothing from the recorder after it
      // is torn down keeps that path beyond question.
      const reportedMimeType = active.mimeType;

      // This recording's own status, written by the listeners start()
      // registered. Read before the stop sequence for the same reason as the
      // MIME type above.
      const status = session;

      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const finished = new Promise<void>((resolve, reject) => {
          const fail = () => {
            reject(new Error('The recording could not be finished.'));
          };

          // AN ERRORED RECORDING IS REFUSED OUTRIGHT (D-017 / D-018): its
          // container is of unknown validity even when a `stop` event also
          // arrived, so it must never resolve into a RecordingResult.
          if (status?.errored) {
            fail();
            return;
          }

          // ALREADY FINALIZED BY THE PLATFORM (S-03-05): when the microphone
          // track ends, the UA stops the recorder itself and the `stop` event
          // this promise would await HAS ALREADY FIRED. `stopped` is only set
          // by that event, which fires after the final `dataavailable` — so
          // the chunks are complete and waiting again would time out and
          // throw away perfectly finalized audio.
          if (status?.stopped) {
            resolve();
            return;
          }

          active.addEventListener('stop', () => {
            resolve();
          });
          // An `error` DURING finalization settles the race the honest way:
          // the audio's validity just became unknowable, so the stop fails
          // rather than quietly resolving into a maybe-corrupt blob (D-018).
          active.addEventListener('error', fail);
          // The bound (I-027 / D-017). Rejecting settles the promise; a `stop`
          // event that limps in later resolves an already-settled promise,
          // which is a no-op.
          timer = setTimeout(fail, stopTimeoutMs);
        });
        // A platform-finalized recorder is already inactive; calling stop() on
        // it throws on some engines. Only nudge a recorder that is still going.
        if (active.state !== 'inactive') active.stop();
        await finished;
      } catch (error) {
        // D-017: no partial-audio salvage in v1. Whatever chunks accumulated
        // are of unknown validity once the recorder failed to finalize, so
        // they are dropped here — the caller gets a clean failure, never a
        // maybe-corrupt blob.
        //
        // This ABANDONS the buffer rather than emptying it (F-1): the timed-out
        // recorder may still be alive and may still fire, and its listener
        // holds the old array directly. Emptying the array in place would hand
        // that listener a buffer this port still reads from. Repointing leaves
        // it writing into something unreachable, which is the whole guarantee.
        chunks = [];
        throw error;
      } finally {
        if (timer !== undefined) clearTimeout(timer);
        releaseMicrophone();
      }

      const elapsedMs = Math.round(performance.now() - startedAt);

      const blob = new Blob(chunks, reportedMimeType ? { type: reportedMimeType } : undefined);
      chunks = [];

      return {
        blob,
        reportedMimeType,
        reportedCodec: parseCodec(reportedMimeType),
        observedContainer: await sniffContainer(blob),
        elapsedMs,
        byteSize: blob.size,
      };
    },

    cancel() {
      try {
        if (recorder && recorder.state !== 'inactive') recorder.stop();
      } catch {
        // Already stopping or already stopped. The microphone still gets released.
      }
      chunks = [];
      releaseMicrophone();
    },
  };
}

/**
 * Chosen once, like every other port.
 *
 * There is one implementation today and that is the finding, not an oversight:
 * Capacitor's WebView handles microphone permission itself, so the web
 * implementation IS the native implementation. If real-hardware acceptance
 * shows otherwise, a native recorder is added HERE and Capture never learns
 * about it.
 */
export function selectRecorder(): RecorderPort {
  return createWebMediaRecorder();
}
